import { logger } from '@adornis/base/logging.js';
import {
  createContractFromTeaching,
  getInstitutionsWithContractsForLecturer,
  subscribeTeachingsForLecturerAndInstitution,
} from '@adornis/buchungsheld-db/api/teaching.js';
import { Teaching } from '@adornis/buchungsheld-db/entities/teaching.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-loader.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/cms/client/generated-form.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { openPopupWithLoadingScreen } from '@adornis/print/client/safe-popup.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { of, switchMap } from 'rxjs';
import { BhPromptAdminManageLecturerContractOnProduct } from '../../prompts/bh-prompt-admin-manage-lecturer-contract-on-product.js';

@customElement('bh-page-profile-contracts-lecturer')
export class BHProfileContractsLecturer extends ChemistryLitElement {
  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  @property({ attribute: 'lecturer-id' }) lecturerID = new RXController<string | null>(this, null);

  @state() private readonly _user = new RXController(this, currentUser<User>());
  @state() private readonly _institutionsWithContracts = new RXController(
    this,
    this.lecturerID.observable.pipe(
      switchMap(lecturerID => (lecturerID ? getInstitutionsWithContractsForLecturer(lecturerID)({ name: 1 }) : of([]))),
    ),
  );

  override render() {
    return html`
      <x-flex space="lg">
        <tr-h3> ${this._translation.translate('PROFILE', 'OVERVIEW_CONTRACTS', 'TITLE')} </tr-h3>
        ${(() => {
          const institutions = this._institutionsWithContracts.value;
          if (institutions?.length > 1) {
            return html`
              ${repeat(
                institutions,
                institution => html`
                  <x-flex space="md">
                    <x-h3> ${institution.name} </x-h3> ${this._dataViewForInstitution(institution._id)}
                  </x-flex>
                `,
              )}
            `;
          } else if (institutions?.length) {
            return html`<x-flex space="md">
              <x-h3> ${institutions[0].name} </x-h3> ${this._dataViewForInstitution(institutions[0]._id)}
            </x-flex>`;
          } else {
            return html`
              <x-empty-placeholder .text=${'Noch keinem Produkt als Dozent zugewiesen.'}></x-empty-placeholder>
            `;
          }
        })()}
      </x-flex>
    `;
  }

  private _dataViewForInstitution(institutionID: string) {
    return html`
      <x-data-view
        .options=${buildFullOptions({
          class: Teaching,
          operation: (skip: number, limit: number) =>
            subscribeTeachingsForLecturerAndInstitution(skip, limit, this.lecturerID.value, institutionID),
          customColumns: [
            { fieldName: 'product.name' },
            { fieldName: 'contractPrice' },
            {
              fieldName: 'signedContractFileID',
              renderItem: teaching =>
                teaching.signedContractFileID
                  ? html`
                      <x-flex horizontal crossaxis-center space="xs">
                        <x-button
                          mode="text"
                          icon="check"
                          tone="success"
                          target="_blank"
                          href=${AdornisFile.getServeLink(teaching.signedContractFileID)}
                        >
                          Vertrag hochgeladen
                        </x-button>
                      </x-flex>
                    `
                  : html`
                      <x-flex horizontal>
                        <x-button mode="text" icon="upload" @click=${() => this._uploadSignedContract(teaching)}>
                          unterschriebenen Vertrag hochladen
                        </x-button>
                      </x-flex>
                    `,
            },
            {
              fieldName: 'contractID',
              columnWidth: 'max-content',
              renderItem: teaching =>
                teaching.contractID
                  ? html`
                      <x-button
                        mode="icon"
                        icon="download"
                        @click=${() => this._downloadEmptyContract(teaching)}
                      ></x-button>
                    `
                  : html` <x-icon> close </x-icon> `,
            },
            ...(this._user.value?.hasPermission(Permission.MANAGE_CONTRACT_TEMPLATES, institutionID)
              ? [
                  {
                    customID: '_options',
                    columnWidth: 'max-content',
                    selectionSet: <const>{ contractPrice: 1, contractPriceWords: 1, contractID: 1 },
                    renderItem: teaching =>
                      html`
                        <tr-button-small
                          icon="contract_edit"
                          @click=${async () => {
                            await BhPromptAdminManageLecturerContractOnProduct.showPopup({
                              props: {
                                hasCloseButton: true,
                                teaching,
                                institutionID,
                              },
                            });

                            try {
                              await TrDialog.waitFor(teaching.save());
                              void XSnackbar.show('Erfolgreich aktualisiert.');
                            } catch (err) {
                              logger.error({ err }, 'there was a problem saving the teaching.');
                              void TrDialog.alert(
                                'Es gab ein Problem beim Speichern. Bitte versuchen Sie es später erneut.',
                              );
                            }
                          }}
                        >
                          <x-tooltip
                            .text=${html`
                              <x-text> ${this._translation.translate('lecturer', 'manageContractOnProduct')} </x-text>
                            `}
                          ></x-tooltip>
                        </tr-button-small>
                      `,
                  },
                ]
              : []),
          ],
        }).dataViewOptions}
      ></x-data-view>
    `;
  }

  private async _uploadSignedContract(teaching: Teaching) {
    const signedContractFile = (await askForFile({ acceptFileTypes: '.pdf' }))?.[0];
    if (!signedContractFile) return;

    try {
      await TrDialog.waitFor(
        AdornisFile.upload(signedContractFile).then(async fileID => {
          teaching.signedContractFileID = fileID;
          await teaching.save();
        }),
      );
    } catch (e) {
      logger.error({ err: e }, 'There was a problem uploading the signed contract');
      TrDialog.alert(
        'Es gab ein Problem beim Hochladen der unterschriebenen Vertrags. Bitte versuche es später noch einmal.',
      );
    }
  }

  private async _downloadEmptyContract(teaching: Teaching) {
    try {
      await openPopupWithLoadingScreen(createContractFromTeaching(teaching._id)());
    } catch (e) {
      logger.error({ err: e }, 'There was a problem downloading the empty contract');
      TrDialog.alert(
        'Es gab ein Problem beim Herunterladen des leeren Vertrags. Bitte versuche es später noch einmal.',
      );
    }
  }
}
